'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { Users, Camera, BookOpen } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

export default function Slide15Values({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0f2318]">
      <div className="absolute inset-0">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1cea8da1e-1772094672439.png"
          alt="Nhóm tình nguyện viên sinh viên cùng nhau làm việc trong không gian xanh, thể hiện tinh thần cộng đồng"
          className="w-full h-full object-cover opacity-8" />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0f2318] via-[#1a3a2a]/30 to-[#0f2318]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[rgba(76,175,125,0.04)] blur-3xl" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="fade-in-up mb-4">
          <div className="chapter-badge">Quyền lợi sinh viên</div>
        </div>

        <div className="fade-in-up mb-3">
          <h2 className="font-display font-800 text-[clamp(28px,4.5vw,56px)] leading-[1.1] text-[#f0ede6] tracking-tight">
            Quyền lợi dành cho sinh viên
          </h2>
        </div>

        <div className="fade-in-up mb-10">
          <div className="section-divider" />
        </div>

        <div className="grid grid-cols-1 gap-5 fade-in-up">
          {/* Card 1: Tạo ra giá trị thật cho cộng đồng */}
          <div className="rounded-2xl p-7 bg-gradient-to-b from-[rgba(76,175,125,0.15)] to-[rgba(76,175,125,0.04)] border border-[rgba(76,175,125,0.25)]">
            <div className="flex items-start gap-5">
              <div className="p-3 rounded-xl bg-[rgba(76,175,125,0.15)] inline-flex flex-shrink-0">
                <Users size={24} className="text-[#4caf7d]" />
              </div>
              <div>
                <h3 className="font-display font-700 text-[18px] lg:text-[20px] text-[#f0ede6] mb-3">Được tạo ra giá trị thật cho cộng đồng</h3>
                <p className="font-body text-[14px] lg:text-[15px] text-[#a0b8a8] leading-relaxed">
                  Những công việc sinh viên thực hiện trong chương trình sẽ tiếp tục được sử dụng cho các hoạt động trải nghiệm dành cho gia đình và trẻ em phía sau. Đây là điểm khiến các bạn không chỉ đi rồi về, mà thực sự để lại dấu ấn của mình tại Trị An.
                </p>
              </div>
            </div>
          </div>

          {/* Card 2: Rèn luyện kỹ năng thực tế */}
          <div className="rounded-2xl p-7 bg-gradient-to-b from-[rgba(232,160,48,0.12)] to-[rgba(232,160,48,0.03)] border border-[rgba(232,160,48,0.22)]">
            <div className="flex items-start gap-5">
              <div className="p-3 rounded-xl bg-[rgba(232,160,48,0.12)] inline-flex flex-shrink-0">
                <BookOpen size={24} className="text-[#e8a030]" />
              </div>
              <div>
                <h3 className="font-display font-700 text-[18px] lg:text-[20px] text-[#f0ede6] mb-3">Rèn luyện kỹ năng thực tế</h3>
                <p className="font-body text-[14px] lg:text-[15px] text-[#a0b8a8] leading-relaxed mb-3">
                  Trong suốt chương trình, sinh viên sẽ có cơ hội rèn luyện các kỹ năng quan trọng như:
                </p>
                <div className="space-y-1.5">
                  {[
                  'Làm việc nhóm',
                  'Tinh thần trách nhiệm',
                  'Sự chủ động',
                  'Tư duy phối hợp',
                  'Khả năng thích nghi trong môi trường thực tế'].
                  map((skill) =>
                  <div key={`skill-${skill}`} className="flex items-center gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#e8a030] flex-shrink-0" />
                      <span className="font-body text-[14px] text-[#a0b8a8]">{skill}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Sản xuất nội dung truyền thông xanh */}
          <div className="rounded-2xl p-7 bg-gradient-to-b from-[rgba(168,216,122,0.12)] to-[rgba(168,216,122,0.03)] border border-[rgba(168,216,122,0.22)]">
            <div className="flex items-start gap-5">
              <div className="p-3 rounded-xl bg-[rgba(168,216,122,0.12)] inline-flex flex-shrink-0">
                <Camera size={24} className="text-[#a8d87a]" />
              </div>
              <div>
                <h3 className="font-display font-700 text-[18px] lg:text-[20px] text-[#f0ede6] mb-3">Được tham gia sản xuất nội dung truyền thông xanh</h3>
                <p className="font-body text-[14px] lg:text-[15px] text-[#a0b8a8] leading-relaxed">
                  Sinh viên không chỉ là người tham gia hoạt động, mà còn có thể trực tiếp cùng nhóm xây dựng video, hình ảnh và câu chuyện truyền thông sau chương trình. Đây là cơ hội phù hợp với những bạn yêu thích truyền thông, sáng tạo nội dung, quay dựng, social media hoặc muốn phát triển hình ảnh cá nhân.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SlideWrapper>);

}